/**
 * Hashtags as every part of the product counts and trims them: the
 * composer's counter (shared-validators countHashtags), the publish
 * formatter and the pre-publish check.
 *
 * A hashtag is `#` at the start of the text or after whitespace, followed
 * by letters, combining marks, digits or `_` of any script (Malayalam,
 * Tamil, Telugu, Kannada, Bengali, Devanagari, Arabic…). Anchored, so the
 * `#section` of a link (`menu.html#section`) is never one.
 */
export const HASHTAG_PATTERN = /(^|\s)#[\p{L}\p{M}\p{N}_]+/gu;

/** One hashtag on a line: where the `#` starts and where the tag ends. */
interface TagSpan {
  start: number;
  end: number;
}

function tagsIn(line: string): TagSpan[] {
  const spans: TagSpan[] = [];
  for (const m of line.matchAll(new RegExp(HASHTAG_PATTERN.source, 'gu'))) {
    const lead = m[1] ?? '';
    const start = (m.index ?? 0) + lead.length;
    spans.push({ start, end: (m.index ?? 0) + m[0].length });
  }
  return spans;
}

/** How many hashtags the text has. */
export function hashtagCount(text: string): number {
  return text.split('\n').reduce((n, line) => n + tagsIn(line).length, 0);
}

/**
 * Keeps the first `keep` hashtags and removes the rest, each with the space
 * before it. Nothing else changes: line and paragraph breaks stay, and a
 * line that held only removed hashtags goes away rather than staying blank.
 */
export function keepFirstHashtags(text: string, keep: number): { text: string; removed: number } {
  let seen = 0;
  let removed = 0;
  const out: string[] = [];
  for (const line of text.split('\n')) {
    const spans = tagsIn(line);
    if (spans.length === 0) {
      out.push(line);
      continue;
    }
    let result = '';
    let cursor = 0;
    let changed = false;
    for (const span of spans) {
      seen += 1;
      if (seen <= keep) continue;
      // The tag and the spaces before it (at the start of a line: the spaces after it).
      let from = span.start;
      while (from > cursor && (line[from - 1] === ' ' || line[from - 1] === '\t')) from -= 1;
      let to = span.end;
      if (from === 0) while (to < line.length && (line[to] === ' ' || line[to] === '\t')) to += 1;
      result += line.slice(cursor, from);
      cursor = to;
      removed += 1;
      changed = true;
    }
    result += line.slice(cursor);
    if (!changed) {
      out.push(line);
    } else if (result.trim() !== '') {
      out.push(result.replace(/[ \t]+$/, ''));
    }
    // A line of nothing but removed hashtags is dropped.
  }
  return { text: removed > 0 ? out.join('\n').trim() : text, removed };
}
